import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Search as SearchIcon, Edit as EditIcon, FileDownload as FileDownloadIcon, FilterAlt as FilterAltIcon, FilterAltOff as FilterAltOffIcon, Visibility as VisibilityIcon, Archive as ArchiveIcon, Delete as DeleteIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment-jalaali';
import { formatJalaliDateDisplay, debugDate, toGregorianISO, parseIsoDateToMoment, momentToIsoDate } from '../utils/dateUtils';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMomentJalaali } from '@mui/x-date-pickers/AdapterMomentJalaali';
import { faIR as faIRPickers } from '@mui/x-date-pickers/locales';
import { API_BASE_URL } from '../config';
import ReportFilterField from './ReportFilterField';
import ListPagination from './ListPagination';
import {
  PAGE_BG_SX,
  HERO_SX,
  HERO_DECOR_1_SX,
  HERO_DECOR_2_SX,
  HERO_CHIP_SX,
  CARD_SX,
  SECTION_TITLE_SX,
  FILTER_SECTION_SX,
  FILTER_INLINE_ROW_SX,
  FILTER_INLINE_LABEL_SX,
  FILTER_GRID_DATES,
  FILTER_GRID_SELECTS,
  FILTER_GRID_EXTRA,
  FILTER_CAPTION_SX,
  FILTER_TEXT_INPUT_SX,
  DATE_PICKER_SLOT_PROPS,
  ACTION_BAR_SX,
  SEARCH_FIELD_SX,
  BTN_OUTLINED_SX,
  BTN_HERO_SX,
  BTN_DANGER_SX,
  TABLE_HEADER_BOX_SX,
  TABLE_HEAD_CELL_SX,
  tableRowSx,
  DIALOG_PAPER_SX,
  DIALOG_TITLE_SX,
  LOADING_BOX_SX,
  PRIMARY,
  PRIMARY_DARK,
} from '../styles/reportPageStyles';

const faIRDateLocaleText = {
  ...faIRPickers.components.MuiLocalizationProvider.defaultProps.localeText,
  fieldYearPlaceholder: () => 'سال',
  fieldMonthPlaceholder: () => 'ماه',
  fieldDayPlaceholder: () => 'روز',
};

const FilterInlineText = ({ label, value, onChange, type = 'text', placeholder }) => (
  <Box sx={FILTER_INLINE_ROW_SX}>
    <Typography
      component="span"
      sx={{
        ...FILTER_INLINE_LABEL_SX,
        color: value ? PRIMARY_DARK : undefined,
      }}
    >
      {label}
    </Typography>
    <TextField
      size="small"
      fullWidth
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      sx={FILTER_TEXT_INPUT_SX}
    />
  </Box>
);
//

const FILTER_PARAM_KEYS = [
  'company', 'product', 'documentType', 'documentNumber', 'location', 'shift',
  'dateFrom', 'dateTo', 'quantityMin', 'quantityMax', 'unit', 'referenceToWaybill', 'description',
];

const DataDisplay = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [archivedEntries, setArchivedEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDescription, setSelectedDescription] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingEntry, setEditingEntry] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // Get current user ID - ensure it's a number for comparison
  const currentUserId = user.id ? Number(user.id) : (user.userId ? Number(user.userId) : null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const updateSearchParams = useCallback((updates, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, val]) => {
      if (val === '' || val == null || val === undefined) next.delete(key);
      else next.set(key, String(val));
    });
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  const searchTerm = searchParams.get('q') || '';
  const page = Number(searchParams.get('page') || 0);
  const rowsPerPage = Number(searchParams.get('pageSize') || 10);
  const showFilters = searchParams.get('filters') === '1';
  const showArchived = searchParams.get('archived') === '1';

  const parseDateParam = useCallback((key) => {
    return parseIsoDateToMoment(searchParams.get(key));
  }, [searchParams]);

  const filters = useMemo(() => ({
    company: searchParams.get('company') || '',
    product: searchParams.get('product') || '',
    documentType: searchParams.get('documentType') || '',
    documentNumber: searchParams.get('documentNumber') || '',
    location: searchParams.get('location') || '',
    shift: searchParams.get('shift') || '',
    dateFrom: parseDateParam('dateFrom'),
    dateTo: parseDateParam('dateTo'),
    quantityMin: searchParams.get('quantityMin') || '',
    quantityMax: searchParams.get('quantityMax') || '',
    unit: searchParams.get('unit') || '',
    referenceToWaybill: searchParams.get('referenceToWaybill') || '',
    description: searchParams.get('description') || '',
  }), [searchParams, parseDateParam]);

  // Helper function to check if user can edit entry
  const canEditEntry = (entry) => {
    console.log('canEditEntry check:', {
      entryId: entry.id,
      documentNumber: entry.documentNumber,
      userRole: user.role,
      currentUserId: currentUserId,
      entryUserId: entry.userId,
      entryCreatedAt: entry.createdAt,
      isEdit: entry.isEdit,
      userIdMatch: entry.userId === currentUserId
    });
    
    // Admin can always edit (except if already edited)
    if (user.role === 'admin') {
      return !entry.isEdit;
    }
    
    // Regular users can only edit their own entries
    // Convert both to numbers for comparison (in case one is string and one is number)
    const entryUserId = entry.userId ? Number(entry.userId) : null;
    const userId = currentUserId ? Number(currentUserId) : null;
    
    if (!entryUserId || !userId || entryUserId !== userId) {
      console.log('User ID mismatch or missing:', { entryUserId, userId });
      return false;
    }
    
    // Can't edit if already edited
    if (entry.isEdit) {
      console.log('Entry already edited');
      return false;
    }
    
    // Check if entry was created within the last 30 minutes
    if (entry.createdAt) {
      try {
        // Parse the ISO date string (backend sends UTC time)
        const createdAt = new Date(entry.createdAt);
        const now = new Date();
        
        // Check if date is valid
        if (isNaN(createdAt.getTime())) {
          console.warn('Invalid createdAt date:', entry.createdAt);
          // If date is invalid, allow edit (for old entries without createdAt)
          return true;
        }
        
        // Calculate difference in minutes (both dates are in UTC, so this should work correctly)
        const diffMs = now.getTime() - createdAt.getTime();
        const diffMinutes = diffMs / (1000 * 60);
        
        console.log('Time check:', {
          createdAt: entry.createdAt,
          parsedDate: createdAt.toISOString(),
          now: now.toISOString(),
          diffMs: diffMs,
          diffMinutes: diffMinutes.toFixed(2),
          canEdit: diffMinutes <= 30
        });
        
        // Allow edit if less than or equal to 30 minutes
        if (diffMinutes > 30) {
          console.log('Entry is too old to edit:', diffMinutes.toFixed(2), 'minutes');
          return false;
        }
        
        console.log('Entry can be edited - within 30 minutes');
        return true;
      } catch (error) {
        console.error('Error checking createdAt:', error, entry.createdAt);
        // If there's an error parsing, allow edit (for old entries)
        return true;
      }
    } else {
      // If createdAt doesn't exist (old entries), allow edit
      console.log('Entry has no createdAt, allowing edit:', entry.id);
      return true;
    }
  };

  // Excel export function
  const exportToExcel = () => {
    const exportData = filteredEntries.map(entry => ({
      'شماره سند': entry.documentNumber || '',
      'شرکت': entry.company || '',
      'محصول': entry.product || '',
      'نوع سند': entry.documentType || '',
      'محل': entry.location || '',
      'شیفت': formatJalaliDateDisplay(entry.date) || '',
      'مقدار': entry.quantity || '',
      'واحد': entry.unit || '',
      'ارجاع به حواله': entry.referenceToWaybill || '',
      'عیار (%)': entry.assayPercent !== null && entry.assayPercent !== undefined ? entry.assayPercent : '',
      'توضیحات': entry.description || ''
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const colWidths = [
      { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 },
      { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
      { wch: 30 }
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'گزارش اطلاعات');

    // Generate filename with Persian date
    const now = new Date();
    const persianDate = now.toLocaleDateString('fa-IR');
    const filename = `گزارش_اطلاعات_${persianDate.replace(/\//g, '-')}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  // Function to refresh data
  const refreshData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Fetch active entries
      const response = await fetch(`${API_BASE_URL}/entries/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('خطا در دریافت اطلاعات');
      }

      const data = await response.json();
      console.log('Refreshed data:', data);
      setEntries(data);

      // Fetch archived entries (only for admin)
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.role === 'admin') {
        const archivedResponse = await fetch(`${API_BASE_URL}/entries/archived/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (archivedResponse.ok) {
          const archivedData = await archivedResponse.json();
          console.log('Refreshed archived data:', archivedData);
          setArchivedEntries(archivedData);
        }
      }
    } catch (error) {
      setError(error.message);
    }
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Fetch active entries
        const response = await fetch(`${API_BASE_URL}/entries/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('خطا در دریافت اطلاعات');
        }

        const data = await response.json();
        console.log('Received data:', data);
        setEntries(data);

        // Fetch archived entries (only for admin)
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.role === 'admin') {
          const archivedResponse = await fetch(`${API_BASE_URL}/entries/archived/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (archivedResponse.ok) {
            const archivedData = await archivedResponse.json();
            console.log('Received archived data:', archivedData);
            setArchivedEntries(archivedData);
          }
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Add focus event listener to refresh data when user returns to the page
  useEffect(() => {
    const handleFocus = () => {
      refreshData();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshData]);

  // Refresh data when component becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshData]);

  // Refresh data when user navigates back to this page
  useEffect(() => {
    const handlePopState = () => {
      refreshData();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [refreshData]);

  // Check for entry updates when component mounts or becomes visible
  useEffect(() => {
    const checkForUpdates = () => {
      // Check if there's a flag indicating an entry was updated
      const entryUpdated = localStorage.getItem('entryUpdated');
      if (entryUpdated === 'true') {
        console.log('Entry was updated, refreshing data...');
        refreshData();
        localStorage.removeItem('entryUpdated');
      }
    };

    // Check immediately when component mounts
    checkForUpdates();

    // Also check when window becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshData]);

  // Additional refresh mechanism - check for updates every time the component is rendered
  useEffect(() => {
    const interval = setInterval(() => {
      const entryUpdated = localStorage.getItem('entryUpdated');
      if (entryUpdated === 'true') {
        console.log('Entry update detected, refreshing data...');
        refreshData();
        localStorage.removeItem('entryUpdated');
      }
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [refreshData]);

  const handleSearch = (event) => {
    updateSearchParams({ q: event.target.value, page: '' }, { replace: true });
  };

  const handleFilterChange = (field, value) => {
    if (field === 'dateFrom' || field === 'dateTo') {
      if (!value) {
        updateSearchParams({ [field]: '', page: '' });
        return;
      }
      // Ignore incomplete / invalid DatePicker drafts (they show as 0000/NaN/NaN).
      const iso = momentToIsoDate(value);
      if (iso) updateSearchParams({ [field]: iso, page: '' });
      return;
    }
    updateSearchParams({ [field]: value ?? '', page: '' });
  };

  const clearFilters = () => {
    const cleared = { q: '', page: '' };
    FILTER_PARAM_KEYS.forEach((k) => { cleared[k] = ''; });
    updateSearchParams(cleared);
  };

  const setPage = (valOrFn) => {
    const nextPage = typeof valOrFn === 'function' ? valOrFn(page) : valOrFn;
    updateSearchParams({ page: nextPage > 0 ? String(nextPage) : '' });
  };

  // Choose which entries to display based on showArchived state
  const currentEntries = showArchived ? archivedEntries : entries;
  
  const filteredEntries = currentEntries.filter((entry) => {
    // General search term
    if (searchTerm) {
    const searchValue = searchTerm.toLowerCase();
      const matchesSearch = (
      (entry.company || '').toLowerCase().includes(searchValue) ||
      (entry.product || '').toLowerCase().includes(searchValue) ||
      (entry.documentType || '').toLowerCase().includes(searchValue) ||
      (entry.location || '').toLowerCase().includes(searchValue) ||
      (entry.shift || '').toLowerCase().includes(searchValue) ||
      (entry.date || '').toLowerCase().includes(searchValue) ||
      (entry.quantity || '').toString().toLowerCase().includes(searchValue) ||
      (entry.referenceToWaybill || '').toLowerCase().includes(searchValue) ||
      (entry.description || '').toLowerCase().includes(searchValue)
    );
      if (!matchesSearch) return false;
    }

    // Specific filters
    if (filters.company && !(entry.company || '').toLowerCase().includes(filters.company.toLowerCase())) {
      return false;
    }
    if (filters.product && !(entry.product || '').toLowerCase().includes(filters.product.toLowerCase())) {
      return false;
    }
    if (filters.documentType && !(entry.documentType || '').toLowerCase().includes(filters.documentType.toLowerCase())) {
      return false;
    }
    if (filters.documentNumber && !(entry.documentNumber || '').toLowerCase().includes(filters.documentNumber.toLowerCase())) {
      return false;
    }
    if (filters.location && !(entry.location || '').toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    if (filters.shift && !(entry.shift || '').toLowerCase().includes(filters.shift.toLowerCase())) {
      return false;
    }
    if (filters.unit && entry.unit !== filters.unit) {
      return false;
    }
    if (filters.referenceToWaybill && !(entry.referenceToWaybill || '').toLowerCase().includes(filters.referenceToWaybill.toLowerCase())) {
      return false;
    }
    if (filters.description && !(entry.description || '').toLowerCase().includes(filters.description.toLowerCase())) {
      return false;
    }

    // Date filters
    if (filters.dateFrom || filters.dateTo) {
      // Convert entry date (ISO YYYY-MM-DD) to comparable string
      const entryISO = toGregorianISO(entry.date);
      if (!entryISO) return false;

      if (filters.dateFrom) {
        const fromISO = toGregorianISO(filters.dateFrom);
        if (fromISO && entryISO < fromISO) return false;
      }
      if (filters.dateTo) {
        const toISO = toGregorianISO(filters.dateTo);
        if (toISO && entryISO > toISO) return false;
      }
    }

    // Quantity filters
    if (filters.quantityMin && entry.quantity < parseFloat(filters.quantityMin)) {
      return false;
    }
    if (filters.quantityMax && entry.quantity > parseFloat(filters.quantityMax)) {
      return false;
    }

    return true;
  });

  const safePageSize = [10, 25, 50].includes(rowsPerPage) ? rowsPerPage : 10;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredEntries.length / safePageSize) - 1);
    if (filteredEntries.length === 0 && page !== 0) {
      updateSearchParams({ page: '' }, { replace: true });
      return;
    }
    if (page > maxPage) {
      updateSearchParams({ page: maxPage > 0 ? String(maxPage) : '' }, { replace: true });
    }
    if (![10, 25, 50].includes(rowsPerPage)) {
      updateSearchParams({ pageSize: '' }, { replace: true });
    }
  }, [filteredEntries.length, page, safePageSize, rowsPerPage, updateSearchParams]);

  const filterLists = useMemo(() => {
    const uniq = (key) => Array.from(new Set(entries.map((e) => e[key]).filter(Boolean)));
    let customUnits = [];
    try {
      const saved = localStorage.getItem('units');
      const parsed = saved ? JSON.parse(saved) : [];
      if (Array.isArray(parsed)) customUnits = parsed;
    } catch {
      /* ignore */
    }
    return {
      company: uniq('company'),
      product: uniq('product'),
      documentType: uniq('documentType'),
      location: uniq('location'),
      shift: uniq('shift'),
      unit: Array.from(new Set(['تن', 'کیلوگرم', 'گرم', ...customUnits, ...uniq('unit')])),
    };
  }, [entries]);

  const handleDescriptionClick = (description) => {
    setSelectedDescription(description);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDescription(null);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingEntry(null);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const deleteSelected = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm('آیا از حذف رکوردهای انتخاب‌شده مطمئن هستید؟')) return;
    try {
      const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/entries/bulk-delete/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      if (!res.ok) throw new Error('خطا در حذف رکوردها');
      // Refresh list
      setEntries((prev) => prev.filter(e => !selectedIds.has(e.id)));
      setSelectedIds(new Set());
    } catch (e) {
      alert('حذف ناموفق بود');
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...PAGE_BG_SX, ...LOADING_BOX_SX }}>
        <CircularProgress size={48} sx={{ color: PRIMARY }} />
        <Typography color="text.secondary">در حال بارگذاری...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...PAGE_BG_SX, py: 4, px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => {
    if (k === 'dateFrom' || k === 'dateTo') return !!v;
    return v !== '' && v != null;
  }).length;

  return (
    <Box sx={PAGE_BG_SX}>
      {/* Hero */}
      <Box sx={HERO_SX}>
        <Box sx={HERO_DECOR_1_SX} />
        <Box sx={HERO_DECOR_2_SX} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.5 }}>
                نمایش اطلاعات
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {showArchived ? 'مشاهده سندهای بایگانی‌شده (فقط خواندنی)' : 'مشاهده، فیلتر و مدیریت اطلاعات ثبت‌شده'}
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={exportToExcel} sx={BTN_HERO_SX}>
              خروجی Excel
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.5 }}>
            <Chip label={`${filteredEntries.length} رکورد`} sx={HERO_CHIP_SX} />
            <Chip label={`صفحه ${page + 1}`} sx={HERO_CHIP_SX} />
            {showArchived && <Chip label="حالت بایگانی" sx={HERO_CHIP_SX} />}
            {activeFilterCount > 0 && <Chip label={`${activeFilterCount} فیلتر فعال`} sx={HERO_CHIP_SX} />}
          </Box>
        </Box>
      </Box>

      {/* Search & Actions */}
      <Card sx={{ ...CARD_SX, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="subtitle2" sx={{ ...SECTION_TITLE_SX, mb: 2 }}>جستجو و عملیات</Typography>
          <Box sx={ACTION_BAR_SX}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="جستجو در تمام فیلدها..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: PRIMARY }} />
                  </InputAdornment>
                ),
              }}
              sx={SEARCH_FIELD_SX}
            />
            <Button variant="outlined" onClick={() => updateSearchParams({ filters: showFilters ? '' : '1' })} startIcon={<FilterAltIcon />} sx={BTN_OUTLINED_SX}>
              فیلتر پیشرفته
            </Button>
            {user.role === 'admin' && (
              <Button
                variant="outlined"
                onClick={() => updateSearchParams({ archived: showArchived ? '' : '1', page: '' })}
                startIcon={showArchived ? <VisibilityIcon /> : <ArchiveIcon />}
                sx={{
                  ...BTN_OUTLINED_SX,
                  ...(showArchived && { borderColor: '#fdab3d', color: '#fdab3d', '&:hover': { borderColor: '#fdab3d', bgcolor: 'rgba(245, 158, 11, 0.06)' } }),
                }}
              >
                {showArchived ? 'سندهای فعال' : 'سندهای بایگانی'}
              </Button>
            )}
            {user.role === 'admin' && !showArchived && (
              <Button
                variant="contained"
                disabled={!selectedIds.size}
                onClick={deleteSelected}
                startIcon={<DeleteIcon />}
                sx={{
                  ...BTN_DANGER_SX,
                  opacity: selectedIds.size ? 1 : 0.5,
                }}
              >
                حذف انتخاب‌شده‌ها ({selectedIds.size})
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <Card sx={{ ...CARD_SX, mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
              <Typography variant="subtitle2" sx={SECTION_TITLE_SX}>فیلترهای پیشرفته</Typography>
              <Button size="small" startIcon={<FilterAltOffIcon />} onClick={clearFilters} sx={BTN_OUTLINED_SX}>
                پاک کردن همه
              </Button>
            </Box>
            {(() => {
              const activeEntries = Object.entries(filters).filter(([k, v]) => {
                if (k === 'dateFrom' || k === 'dateTo') return !!v;
                return v !== '' && v != null;
              });
              if (!activeEntries.length) return null;
              return (
                <Box sx={{
                  mb: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: '#f7f8fa',
                  border: '1px solid #e6e9ef',
                  direction: 'rtl',
                }}>
                  {activeEntries.map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${{
                        company: 'شرکت',
                        product: 'محصول',
                        documentType: 'نوع سند',
                        documentNumber: 'شماره سند',
                        location: 'محل',
                        shift: 'شیفت',
                        dateFrom: 'از تاریخ',
                        dateTo: 'تا تاریخ',
                        quantityMin: 'حداقل مقدار',
                        quantityMax: 'حداکثر مقدار',
                        unit: 'واحد',
                        referenceToWaybill: 'ارجاع به حواله',
                        description: 'توضیحات'
                      }[key] || key}: ${
                        (key === 'dateFrom' || key === 'dateTo') ? (formatJalaliDateDisplay(value) || '-') : (value || '-')
                      }`}
                      onDelete={() => handleFilterChange(key, (key === 'dateFrom' || key === 'dateTo') ? null : '')}
                      sx={{ direction: 'rtl', fontWeight: 600, borderColor: 'rgba(0,168,106,0.3)', color: PRIMARY_DARK }}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              );
            })()}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={FILTER_SECTION_SX}>
                <Typography variant="caption" sx={FILTER_CAPTION_SX}>بازه زمانی</Typography>
                <LocalizationProvider dateAdapter={AdapterMomentJalaali} localeText={faIRDateLocaleText}>
                  <Box sx={FILTER_GRID_DATES}>
                    <Box sx={FILTER_INLINE_ROW_SX}>
                      <Typography component="span" sx={FILTER_INLINE_LABEL_SX}>از تاریخ</Typography>
                      <DesktopDatePicker
                        format="jYYYY/jMM/jDD"
                        value={filters.dateFrom}
                        onChange={(val) => handleFilterChange('dateFrom', val)}
                        maxDate={filters.dateTo || undefined}
                        slotProps={DATE_PICKER_SLOT_PROPS}
                      />
                    </Box>
                    <Box sx={FILTER_INLINE_ROW_SX}>
                      <Typography component="span" sx={FILTER_INLINE_LABEL_SX}>تا تاریخ</Typography>
                      <DesktopDatePicker
                        format="jYYYY/jMM/jDD"
                        value={filters.dateTo}
                        onChange={(val) => handleFilterChange('dateTo', val)}
                        minDate={filters.dateFrom || undefined}
                        slotProps={DATE_PICKER_SLOT_PROPS}
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>

              <Box sx={FILTER_SECTION_SX}>
                <Typography variant="caption" sx={FILTER_CAPTION_SX}>فیلتر داده‌ها</Typography>
                <Box sx={FILTER_GRID_SELECTS}>
                  {[
                    { label: 'شرکت', key: 'company', list: filterLists.company },
                    { label: 'محصول', key: 'product', list: filterLists.product },
                    { label: 'نوع سند', key: 'documentType', list: filterLists.documentType },
                    { label: 'محل', key: 'location', list: filterLists.location },
                    { label: 'شیفت', key: 'shift', list: filterLists.shift },
                    { label: 'واحد', key: 'unit', list: filterLists.unit },
                  ].map((f) => (
                    <ReportFilterField
                      key={f.key}
                      label={f.label}
                      value={filters[f.key]}
                      onChange={(val) => updateSearchParams({ [f.key]: val, page: '' })}
                      options={f.list}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={FILTER_SECTION_SX}>
                <Typography variant="caption" sx={FILTER_CAPTION_SX}>جزئیات بیشتر</Typography>
                <Box sx={FILTER_GRID_EXTRA}>
                  <FilterInlineText
                    label="شماره سند"
                    value={filters.documentNumber}
                    onChange={(e) => handleFilterChange('documentNumber', e.target.value)}
                    placeholder="جستجو..."
                  />
                  <FilterInlineText
                    label="حداقل (کیلو)"
                    type="number"
                    value={filters.quantityMin}
                    onChange={(e) => handleFilterChange('quantityMin', e.target.value)}
                    placeholder="حداقل مقدار"
                  />
                  <FilterInlineText
                    label="حداکثر (کیلو)"
                    type="number"
                    value={filters.quantityMax}
                    onChange={(e) => handleFilterChange('quantityMax', e.target.value)}
                    placeholder="حداکثر مقدار"
                  />
                  <FilterInlineText
                    label="ارجاع به حواله"
                    value={filters.referenceToWaybill}
                    onChange={(e) => handleFilterChange('referenceToWaybill', e.target.value)}
                    placeholder="شماره حواله"
                  />
                  <FilterInlineText
                    label="توضیحات"
                    value={filters.description}
                    onChange={(e) => handleFilterChange('description', e.target.value)}
                    placeholder="متن توضیحات"
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card sx={{ ...CARD_SX, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={TABLE_HEADER_BOX_SX}>
            <Typography variant="subtitle2" sx={{ ...SECTION_TITLE_SX, mb: 1.5, px: 1 }}>لیست رکوردها</Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: user.role === 'admin' && !showArchived ? '40px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
              gap: 2,
              alignItems: 'center',
              px: 1,
            }}>
              {user.role === 'admin' && !showArchived && (
                <Typography sx={TABLE_HEAD_CELL_SX}>انتخاب</Typography>
              )}
              {['شرکت', 'محصول', 'نوع سند', 'محل', 'شیفت', 'تاریخ', 'مقدار (کیلو)', 'واحد', 'ارجاع به حواله', 'توضیحات', 'عیار', 'عملیات'].map((col) => (
                <Typography key={col} sx={TABLE_HEAD_CELL_SX}>{col}</Typography>
              ))}
            </Box>
          </Box>

          {/* Table Body */}
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {filteredEntries
              .slice(page * safePageSize, page * safePageSize + safePageSize)
              .map((entry, index) => (
                <Box
                  key={entry.id}
                  sx={{
                    ...tableRowSx(index),
                    gridTemplateColumns: user.role === 'admin' && !showArchived ? '40px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
                  }}
                >
                  {user.role === 'admin' && !showArchived && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedIds.has(entry.id)} 
                        onChange={() => toggleSelect(entry.id)}
                        style={{
                          width: '18px',
                          height: '18px',
                          accentColor: '#00a86a'
                        }}
                      />
                    </Box>
                  )}
                  <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500 }}>
                    {entry.company || '-'}
                  </Typography>
                  <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#323338', fontWeight: 500 }}>
                      {entry.product || '-'}
                    </Typography>
                  </Box>
                  <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500 }}>
                    {entry.documentType || '-'}
                  </Typography>
                  <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500 }}>
                    {entry.location || '-'}
                  </Typography>
                  <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500 }}>
                    {entry.shift === 'Shutdown' ? 'خاموشی' : (entry.shift || '-')}
                  </Typography>
                  <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500 }}>
                    {(() => {
                      // Debug date formatting
                      if (entry.date) {
                        debugDate(entry.date, `Entry ${entry.id} Date`);
                      }
                      return formatJalaliDateDisplay(entry.date) || '-';
                    })()}
                  </Typography>
                  <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500 }}>
                    {entry.quantity || '-'}
                  </Typography>
                  <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500 }}>
                    {entry.unit || 'کیلوگرم'}
                  </Typography>
                  <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500 }}>
                    {entry.referenceToWaybill || '-'}
                  </Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    {entry.description && (
                      <Button
                        variant="outlined"
                        onClick={() => handleDescriptionClick(entry.description)}
                        sx={{ 
                          textTransform: 'none',
                          borderRadius: '8px',
                          borderColor: '#00a86a',
                          color: '#00a86a',
                          fontWeight: 600,
                          px: 2,
                          py: 0.5,
                          fontSize: '0.8rem',
                          minWidth: '100px',
                          '&:hover': {
                            borderColor: '#0073ea',
                            backgroundColor: 'rgba(0, 168, 106, 0.05)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        مشاهده توضیحات
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    {entry.assayPercent !== null && entry.assayPercent !== undefined ? (
                      <Box sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: '#e5f4ff',
                        color: '#0073ea',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}>
                        <span>{entry.assayPercent}%</span>
                      </Box>
                    ) : (
                      <Typography sx={{ color: '#c5c7d0', fontSize: '0.8rem' }}>
                        -
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    {showArchived ? (
                      <Box sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: '#fff4e0',
                        color: '#fdab3d',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}>
                        🗄️ بایگانی شده
                      </Box>
                    ) : canEditEntry(entry) ? (
                      <Button
                        variant="outlined"
                        onClick={() => handleEditEntry(entry)}
                        sx={{ 
                          textTransform: 'none',
                          borderRadius: '8px',
                          borderColor: '#00a86a',
                          color: '#00a86a',
                          fontWeight: 600,
                          px: 2,
                          py: 0.5,
                          fontSize: '0.8rem',
                          minWidth: '100px',
                          '&:hover': {
                            borderColor: '#037f4c',
                            backgroundColor: 'rgba(0, 168, 106, 0.05)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        startIcon={<EditIcon />}
                      >
                        ویرایش
                      </Button>
                    ) : entry.isEdit ? (
                      <Box sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: '#e6e9ef',
                        color: '#676879',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}>
                        ویرایش شده
                      </Box>
                    ) : entry.userId && entry.userId !== currentUserId ? (
                      <Box sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: '#fde8eb',
                        color: '#c9364a',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}>
                        فقط مالک
                      </Box>
                    ) : entry.createdAt ? (
                      <Box sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: '#fde8eb',
                        color: '#c9364a',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}>
                        زمان گذشته
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              ))}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ ...CARD_SX, mt: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <ListPagination
            page={page}
            pageCount={filteredEntries.length === 0 ? 0 : Math.ceil(filteredEntries.length / safePageSize)}
            totalCount={filteredEntries.length}
            pageSize={safePageSize}
            showPageSize
            onPageChange={(next) => setPage(next)}
            onPageSizeChange={(size) => {
              updateSearchParams({
                pageSize: size === 10 ? '' : String(size),
                page: '',
              });
            }}
          />
        </CardContent>
      </Card>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={DIALOG_TITLE_SX}>
          توضیحات
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 4,
              minHeight: '200px',
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: '100%',
                background: 'rgba(248,249,250,0.8)',
                borderRadius: '12px',
                border: '1px solid rgba(50, 51, 56,0.04)',
                p: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(0,122,255,0.3), transparent)',
                }
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  lineHeight: 1.7,
                  color: '#323338',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  textAlign: 'right',
                  direction: 'rtl',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                }}
              >
                {selectedDescription || 'توضیحاتی موجود نیست'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 3,
            background: 'rgba(248,249,250,0.5)',
            borderTop: '1px solid rgba(50, 51, 56,0.06)',
            justifyContent: 'center'
          }}
        >
          <Button onClick={handleCloseDialog} variant="outlined" sx={BTN_OUTLINED_SX}>
            بستن
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: '0 32px 64px rgba(50, 51, 56,0.12), 0 0 0 1px rgba(255,255,255,0.05)',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.2)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #00a86a 0%, #037f4c 100%)',
            color: 'white',
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '1.25rem',
            py: 3,
            px: 3,
            mb: 0,
            borderBottom: '1px solid rgba(50, 51, 56,0.06)',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '40px',
              height: '3px',
              background: 'linear-gradient(90deg, #00a86a, #037f4c)',
              borderRadius: '2px',
            }
          }}
        >
          ✏️ ویرایش رکورد
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 3,
            background: 'rgba(248,249,250,0.5)',
            borderRadius: '12px',
            p: 3,
            border: '1px solid rgba(50, 51, 56,0.05)'
          }}>
            <Typography sx={{ fontWeight: 600, color: '#323338', textAlign: 'center' }}>
              آیا می‌خواهید این رکورد را ویرایش کنید؟
            </Typography>
            {editingEntry && (
              <Box sx={{ 
                background: 'white',
                borderRadius: '8px',
                p: 3,
                border: '1px solid rgba(50, 51, 56,0.1)'
              }}>
                <Typography sx={{ fontSize: '1rem', color: '#323338', fontWeight: 600, mb: 2, textAlign: 'center' }}>
                  اطلاعات رکورد انتخابی:
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Typography sx={{ fontSize: '0.9rem', color: '#323338' }}>
                    <strong>🏢 شرکت:</strong> {editingEntry.company || '-'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#323338' }}>
                    <strong>📦 محصول:</strong> {editingEntry.product || '-'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#323338' }}>
                    <strong>🔢 شماره سند:</strong> {editingEntry.documentNumber || '-'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#323338' }}>
                    <strong>📍 محل:</strong> {editingEntry.location || '-'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#323338' }}>
                    <strong>⏰ شیفت:</strong> {editingEntry.shift === 'Shutdown' ? 'خاموشی' : (editingEntry.shift || '-')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#323338' }}>
                    <strong>📅 تاریخ:</strong> {formatJalaliDateDisplay(editingEntry.date) || '-'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#323338' }}>
                    <strong>🔢 مقدار:</strong> {editingEntry.quantity || '-'} {editingEntry.unit || ''}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#323338' }}>
                    <strong>📝 توضیحات:</strong> {editingEntry.description ? (editingEntry.description.length > 30 ? editingEntry.description.substring(0, 30) + '...' : editingEntry.description) : '-'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 3, 
            background: 'rgba(248,249,250,0.5)',
            borderRadius: '0 0 20px 20px',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <Button 
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              borderColor: '#676879',
              color: '#676879',
              fontWeight: 600,
              px: 3,
              py: 1,
              '&:hover': {
                borderColor: '#676879',
                backgroundColor: 'rgba(103, 104, 121, 0.05)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            انصراف
          </Button>
          <Button 
            onClick={() => {
              handleCloseEditDialog();
              navigate(`/entry?edit=${editingEntry.id}`, { state: { editEntry: editingEntry } });
            }}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #00a86a 0%, #037f4c 100%)',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: '8px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(0, 168, 106, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #037f4c 0%, #037f4c 100%)',
                boxShadow: '0 6px 16px rgba(0, 168, 106, 0.4)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            ✏️ ویرایش
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataDisplay; 